/**
 * Projects API service
 */
import axios from 'axios';
import {
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectMember,
  ProjectMemberCreate,
  ProjectMemberUpdate,
  ProjectIdentifier,
  ProjectIdentifierCreate,
} from '../types/project';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Project endpoints

export const listProjects = async (): Promise<Project[]> => {
  const response = await axios.get(`${API_URL}/api/v1/projects`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
  return response.data;
};

export const listProjectsByCompany = async (companyId: number): Promise<Project[]> => {
  const response = await axios.get(`${API_URL}/api/v1/projects/company/${companyId}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
  return response.data;
};

export const getProject = async (id: number): Promise<Project> => {
  const response = await axios.get(`${API_URL}/api/v1/projects/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
  return response.data;
};

export const createProject = async (
  companyId: number,
  data: ProjectCreate
): Promise<Project> => {
  const response = await axios.post(
    `${API_URL}/api/v1/projects`,
    { ...data, company_id: companyId },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
  return response.data;
};

export const updateProject = async (
  id: number,
  data: ProjectUpdate
): Promise<Project> => {
  const response = await axios.put(`${API_URL}/api/v1/projects/${id}`, data, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
  return response.data;
};

export const deleteProject = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/api/v1/projects/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
};

// Project member endpoints

export const getProjectMembers = async (projectId: number): Promise<ProjectMember[]> => {
  const response = await axios.get(`${API_URL}/api/v1/projects/${projectId}/members`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
  return response.data;
};

export const addProjectMember = async (
  projectId: number,
  data: ProjectMemberCreate
): Promise<ProjectMember> => {
  const response = await axios.post(
    `${API_URL}/api/v1/projects/${projectId}/members`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
  return response.data;
};

export const updateProjectMemberRole = async (
  projectId: number,
  userId: number,
  data: ProjectMemberUpdate
): Promise<ProjectMember> => {
  const response = await axios.put(
    `${API_URL}/api/v1/projects/${projectId}/members/${userId}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
  return response.data;
};

export const removeProjectMember = async (
  projectId: number,
  userId: number
): Promise<void> => {
  await axios.delete(`${API_URL}/api/v1/projects/${projectId}/members/${userId}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });
};

// Project identifier endpoints

export const getProjectIdentifiers = async (
  projectId: number
): Promise<ProjectIdentifier[]> => {
  const response = await axios.get(
    `${API_URL}/api/v1/projects/${projectId}/identifiers`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
  return response.data;
};

export const addProjectIdentifier = async (
  projectId: number,
  data: ProjectIdentifierCreate
): Promise<ProjectIdentifier> => {
  const response = await axios.post(
    `${API_URL}/api/v1/projects/${projectId}/identifiers`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
  return response.data;
};

export const deleteProjectIdentifier = async (
  projectId: number,
  identifierId: number
): Promise<void> => {
  await axios.delete(
    `${API_URL}/api/v1/projects/${projectId}/identifiers/${identifierId}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
};

// Project AI Settings endpoints

export interface ProjectAISettings {
  id: number;
  project_id: number;
  response_mode: string;
  default_prompt_id: number | null;
  model_settings: {
    model?: string;
    temperature?: number;
    max_output_tokens?: number;
    reasoning?: { effort?: string };
    tools?: string[];
    [key: string]: any;
  } | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProjectAISettingsUpdate {
  response_mode?: string;
  default_prompt_id?: number | null;
  model_settings?: {
    model?: string;
    temperature?: number;
    max_output_tokens?: number;
    reasoning?: { effort?: string };
    tools?: string[];
    [key: string]: any;
  } | null;
}

export const getProjectAISettings = async (
  projectId: number
): Promise<ProjectAISettings> => {
  const response = await axios.get(
    `${API_URL}/api/v1/projects/${projectId}/ai-settings`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
  return response.data;
};

export const updateProjectAISettings = async (
  projectId: number,
  data: ProjectAISettingsUpdate
): Promise<ProjectAISettings> => {
  const response = await axios.put(
    `${API_URL}/api/v1/projects/${projectId}/ai-settings`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    }
  );
  return response.data;
};
